import React from "react";
import { Box, Typography, Paper, Stack, Button, Chip } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import DescriptionIcon from "@mui/icons-material/Description";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ChecklistIcon from "@mui/icons-material/Checklist";
import SlideshowIcon from "@mui/icons-material/Slideshow";

const guides = [
  {
    id: 1,
    title: "RepAssist Overview Deck",
    description:
      "Full product overview with use cases, screenshots, and workflows.",
    type: "PPT",
    size: "4.2 MB",
    icon: <SlideshowIcon sx={{ color: "#ED6C02" }} />,
    iconBg: "#FFF3E0",
  },
  {
    id: 2,
    title: "Top 10 Prompts to Get Started",
    description: "Copy-paste prompts optimized for HCP prep and product Q&A.",
    type: "PDF",
    size: "1.1 MB",
    icon: <ChecklistIcon sx={{ color: "#D32F2F" }} />,
    iconBg: "#FDEDED",
  },
  {
    id: 3,
    title: "How to Log Calls",
    description:
      "Step-by-step guide to capturing and syncing call notes with your CRM.",
    type: "PDF",
    size: "780 KB",
    icon: <DescriptionIcon sx={{ color: "#396D87" }} />,
    iconBg: "#EAF4F8",
  },
  {
    id: 4,
    title: "Territory Planning Guide",
    description:
      "Best practices for setting up territories and managing HCP lists.",
    type: "DOC",
    size: "2.3 MB",
    icon: <MenuBookIcon sx={{ color: "#1565C0" }} />,
    iconBg: "#E8F1FD",
  },
];

const getTypeStyles = (type) => {
  switch (type) {
    case "PPT":
      return {
        color: "#ED6C02",
        backgroundColor: "#FFF3E0",
        borderColor: "#FFD8B2",
      };
    case "PDF":
      return {
        color: "#D32F2F",
        backgroundColor: "#FDEDED",
        borderColor: "#F5C2C7",
      };
    case "DOC":
      return {
        color: "#1565C0",
        backgroundColor: "#E8F1FD",
        borderColor: "#C5DDF8",
      };
    default:
      return {
        color: "#666666",
        backgroundColor: "#F5F5F5",
        borderColor: "#E0E0E0",
      };
  }
};

function QuickStartGuides() {
  return (
    <Box sx={{ pb: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            color: "#202020",
          }}
        >
          Quick Start Guides
        </Typography>

        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 600,
            color: "#396D87",
            cursor: "pointer",
          }}
        >
          View all
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          border: "1px solid #E6E6E6",
          borderRadius: "16px",
          overflow: "hidden",
          backgroundColor: "#FFFFFF",
        }}
      >
        {guides.map((guide, index) => {
          const typeStyles = getTypeStyles(guide.type);

          return (
            <Box
              key={guide.id}
              sx={{
                px: 2.5,
                py: 2,
                display: "flex",
                alignItems: "center",
                gap: 2,
                borderBottom:
                  index !== guides.length - 1 ? "1px solid #EEEEEE" : "none",
                "&:hover": {
                  backgroundColor: "#FAFAFA",
                },
              }}
            >
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  minWidth: 42,
                  borderRadius: "10px",
                  backgroundColor: guide.iconBg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {guide.icon}
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 0.5, flexWrap: "wrap" }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#202020",
                    }}
                  >
                    {guide.title}
                  </Typography>

                  <Chip
                    label={guide.type}
                    size="small"
                    variant="outlined"
                    sx={{
                      height: "20px",
                      fontSize: "10px",
                      fontWeight: 700,
                      color: typeStyles.color,
                      backgroundColor: typeStyles.backgroundColor,
                      borderColor: typeStyles.borderColor,
                    }}
                  />
                </Stack>

                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#666666",
                    lineHeight: 1.5,
                  }}
                >
                  {guide.description}
                </Typography>
              </Box>

              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ minWidth: "fit-content" }}
              >
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#666666",
                    display: { xs: "none", sm: "block" },
                  }}
                >
                  {guide.size}
                </Typography>

                <Button
                  variant="text"
                  sx={{
                    minWidth: "auto",
                    p: 1,
                    color: "#666666",
                    borderRadius: "8px",
                    "&:hover": {
                      backgroundColor: "#EAF4F8",
                      color: "#396D87",
                    },
                  }}
                >
                  <DownloadIcon fontSize="small" />
                </Button>
              </Stack>
            </Box>
          );
        })}
      </Paper>
    </Box>
  );
}

export default QuickStartGuides;
